import { createSignal, onCleanup } from "solid-js";
import type { ServerMessage } from "@houston/shared";

/**
 * Auto-reconnecting WebSocket primitive. Exposes a SolidJS signal of
 * the latest server message. Components subscribe via createEffect.
 *
 * Reconnect uses exponential backoff capped at 30s. Heartbeat ping
 * runs every 25s to keep Fly.io's idle timeout from killing the connection.
 */
export function createWebSocket(url: string) {
  const [latest, setLatest] = createSignal<ServerMessage | null>(null);
  const [connected, setConnected] = createSignal(false);
  let ws: WebSocket | null = null;
  let attempt = 0;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function connect() {
    ws = new WebSocket(url);

    ws.onopen = () => {
      attempt = 0;
      setConnected(true);
      pingTimer = setInterval(() => {
        ws?.send(JSON.stringify({ type: "ping" }));
      }, 25_000);
    };

    ws.onmessage = (e) => {
      try {
        setLatest(JSON.parse(String(e.data)) as ServerMessage);
      } catch {
        /* ignore malformed */
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (pingTimer) clearInterval(pingTimer);
      const delay = Math.min(30_000, 500 * 2 ** attempt++);
      reconnectTimer = setTimeout(connect, delay);
    };

    ws.onerror = () => ws?.close();
  }

  connect();

  onCleanup(() => {
    if (pingTimer) clearInterval(pingTimer);
    if (reconnectTimer) clearTimeout(reconnectTimer);
    ws?.close();
  });

  return { latest, connected };
}
